import { inject, Injectable } from '@angular/core';
import { FiTaskColumns } from '../fi-task-v2-enums';
import { FiTasksViewMode } from '@fi-shared/fi-task-shared/fi-task-enums';
import { IItemsListSortOption } from '@fi-app/shared/fi-items-list/fi-items-list-sort-select/fi-items-list-sort-select.component';
import { SideListOptionType } from '@fi-shared/types';
import { AppTextService } from '@fi-shared/app-text';
import { FiTaskV2SyncService } from '../fi-task-v2-sync.service';
import { ISideListOptionsItem } from '@fi-app/shared/fi-items-list/fi-items-list';
import { DeepReadonly } from '@fi-app/shared/types/deep-readonly';
import { FiTaskStatusType } from '@fi-app/task-v2/fi-task-v2-enums';

@Injectable()
export class FiTaskV2ListHelperService {
    private readonly appTextService = inject(AppTextService);
    private readonly fiTaskV2SyncService = inject(FiTaskV2SyncService);

    readonly viewModeToDefaultSortColumn: Partial<Record<FiTasksViewMode, FiTaskColumns>> = {
        [FiTasksViewMode.notCompleted]: FiTaskColumns.DeadlineColumn,
        [FiTasksViewMode.completed]: FiTaskColumns.CompletedOnColumn,
        [FiTasksViewMode.cancelled]: FiTaskColumns.DeadlineColumn,
    };

    readonly viewModeToDefaultSortDirection: Partial<Record<FiTasksViewMode, boolean>> = {
        [FiTasksViewMode.notCompleted]: false,
        [FiTasksViewMode.completed]: true,
        [FiTasksViewMode.cancelled]: false,
    };

    readonly sideListOptions: DeepReadonly<ISideListOptionsItem[]> = [
        {
            value: SideListOptionType.TasksAssignedToMe,
            text: this.appTextService.get('task.assigned_to_me'),
            counter$: this.fiTaskV2SyncService.assignedToMeTasksCount$,
        },
        {
            value: SideListOptionType.TasksPrivate,
            text: this.appTextService.get('task.private_tasks'),
            counter$: this.fiTaskV2SyncService.privateTasksCount$,
        },
        {
            value: SideListOptionType.TasksAssignedByMe,
            text: this.appTextService.get('task.assigned_by_me'),
            counter$: this.fiTaskV2SyncService.assignedByMeTasksCount$,
        },
    ];

    readonly archivedSideListOptions: DeepReadonly<ISideListOptionsItem[]> = [
        {
            value: SideListOptionType.SharedWithMe,
            text: this.appTextService.get('task.all_tasks'),
            counter$: this.fiTaskV2SyncService.allTasksCount$,
        },
        ...this.sideListOptions,
    ];

    getSortOptions(viewMode: FiTasksViewMode): IItemsListSortOption[] {
        return [
            {
                columnName: FiTaskColumns.CompletedOnColumn,
                textId: 'task.completion_date',
                defaultSortDirection: true,
                isHidden: () => viewMode !== FiTasksViewMode.completed,
                sortDirectionOptions: [
                    { value: true, textId: 'sort.newest_first' },
                    { value: false, textId: 'sort.oldest_first' },
                ],
            },
            {
                columnName: FiTaskColumns.DeadlineColumn,
                textId: 'task.deadline',
                defaultSortDirection: false,
                sortDirectionOptions: [
                    { value: false, textId: 'sort.nearest_first' },
                    { value: true, textId: 'sort.farthest_first' },
                ],
            },
            {
                columnName: FiTaskColumns.PriorityColumn,
                textId: 'common.priority',
                defaultSortDirection: true,
                sortDirectionOptions: [
                    { value: true, textId: 'sort.high_priority_first' },
                    { value: false, textId: 'sort.low_priority_first' },
                ],
            },
            {
                columnName: FiTaskColumns.StatusColumn,
                textId: 'common.status',
                defaultSortDirection: false,
                isHidden: () => viewMode !== FiTasksViewMode.notCompleted,
                sortDirectionOptions: [
                    { value: false, textId: 'sort.not_started_first' },
                    { value: true, textId: 'sort.in_progress_first' },
                ],
            },
        ];
    }

    shouldRemoveTaskFromList(viewMode: FiTasksViewMode, newStatus: FiTaskStatusType): boolean {
        return (
            viewMode === FiTasksViewMode.completed ||
            viewMode === FiTasksViewMode.cancelled ||
            (viewMode === FiTasksViewMode.notCompleted &&
                (newStatus === FiTaskStatusType.Completed || newStatus === FiTaskStatusType.Cancelled))
        );
    }
}
